// Auth Service - Firebase Authentication with Google (Multi-Account Support)
import { auth } from '../config/firebase.js';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import useAuthStore from '../stores/useAuthStore.js';
import { GOOGLE_SCOPES } from '../config/google-calendar.js';
import { firestoreService } from './firestore.service.js';

// Create Google Auth Provider with Calendar scopes
const createGoogleProvider = () => {
  const provider = new GoogleAuthProvider();
  GOOGLE_SCOPES.forEach(scope => provider.addScope(scope));
  provider.setCustomParameters({
    prompt: 'select_account'
  });
  return provider;
};

// Sign in with Google (primary account or add additional account)
export async function signInWithGoogle() {
  try {
    const googleProvider = createGoogleProvider();
    const result = await signInWithPopup(auth, googleProvider);

    // Get the Google Access Token for Calendar API
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential.accessToken;
    const email = result.user.email;

    // Store the access token for this account
    useAuthStore.getState().setAccountToken(email, accessToken, {
      name: result.user.displayName,
      picture: result.user.photoURL,
      uid: result.user.uid,
    });

    return {
      user: result.user,
      accessToken,
      email
    };
  } catch (error) {
    console.error('Sign-in failed:', error);

    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    }

    throw error;
  }
}

// Add an additional Google account (for multi-account support)
export async function addGoogleAccount() {
  try {
    // Get the primary user's UID before the popup (it will change after)
    const primaryUser = auth.currentUser;
    const primaryUserId = primaryUser?.uid;

    // Create a new provider that forces account selection
    const googleProvider = createGoogleProvider();

    const result = await signInWithPopup(auth, googleProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential.accessToken;
    const email = result.user.email;

    // Check if account already exists in local storage
    const isAlreadyConnected = useAuthStore.getState().isAccountConnected(email);

    // Store/update the access token for this account locally
    useAuthStore.getState().setAccountToken(email, accessToken, {
      name: result.user.displayName,
      picture: result.user.photoURL,
      uid: result.user.uid,
    });

    // Persist token to Firestore so it survives page reloads
    if (primaryUserId) {
      try {
        await firestoreService.saveGoogleToken(primaryUserId, email, accessToken);
      } catch (e) {
        // Non-blocking — token is already in memory
      }
    }

    // Save linked account to Firestore under the primary user's document
    if (primaryUserId && !isAlreadyConnected) {
      try {
        await firestoreService.addLinkedAccountToSubcollection(primaryUserId, {
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
          uid: result.user.uid,
        });
      } catch (firestoreError) {
        // Continue anyway - the local storage still has the token
      }
    }

    return {
      user: result.user,
      accessToken,
      email,
      isNewAccount: !isAlreadyConnected
    };
  } catch (error) {
    console.error('Add account failed:', error);

    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Add account cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
    }

    throw error;
  }
}

// Refresh access token for a specific account
export async function refreshAccountToken(email) {
  try {
    const googleProvider = createGoogleProvider();
    // Hint to use the specific account
    googleProvider.setCustomParameters({
      prompt: 'none',
      login_hint: email
    });

    const result = await signInWithPopup(auth, googleProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential.accessToken;

    // Only update if this is the expected account
    if (result.user.email === email) {
      useAuthStore.getState().setAccountToken(email, accessToken, {
        name: result.user.displayName,
        picture: result.user.photoURL,
        uid: result.user.uid,
      });
      // Persist refreshed token to Firestore
      const currentUser = auth.currentUser;
      if (currentUser) {
        firestoreService.saveGoogleToken(currentUser.uid, email, accessToken).catch(() => {});
      }
      return { accessToken, email };
    } else {
      // User selected a different account, store that one too
      useAuthStore.getState().setAccountToken(result.user.email, accessToken, {
        name: result.user.displayName,
        picture: result.user.photoURL,
        uid: result.user.uid,
      });
      // Persist refreshed token to Firestore
      const currentUser = auth.currentUser;
      if (currentUser) {
        firestoreService.saveGoogleToken(currentUser.uid, result.user.email, accessToken).catch(() => {});
      }
      return { accessToken, email: result.user.email };
    }
  } catch (error) {
    // If silent refresh fails, prompt for re-authentication
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      throw new Error(`Please sign in again to refresh access for ${email}`);
    }
    console.error('Token refresh failed:', error);
    throw error;
  }
}

// Disconnect a specific Google account
export async function disconnectAccount(email) {
  // Remove from local storage
  const remaining = useAuthStore.getState().removeAccount(email);

  // Remove from Firestore subcollection
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      await firestoreService.removeLinkedAccountFromSubcollection(currentUser.uid, email);
    } catch (error) {
      console.error('Failed to remove linked account from Firestore:', error);
    }
  }

  return Object.keys(remaining).length;
}

// Sign out completely (all accounts)
export async function signOutUser() {
  try {
    await firebaseSignOut(auth);
    useAuthStore.getState().signOut();
    sessionStorage.clear();
  } catch (error) {
    console.error('Sign-out failed:', error);
    throw error;
  }
}

// Get current user
export function getCurrentUser() {
  return auth.currentUser;
}

// Check if user is authenticated
export function isAuthenticated() {
  return !!auth.currentUser;
}

// Get Google Access Token (primary account - for backward compatibility)
export function getGoogleAccessToken() {
  return useAuthStore.getState().getGoogleAccessToken();
}

// Get token for a specific account
export function getAccountToken(email) {
  return useAuthStore.getState().getAccountToken(email);
}

// Get all connected accounts
export function getConnectedAccounts() {
  return useAuthStore.getState().getConnectedAccounts();
}

// Check if a specific account is connected
export function isAccountConnected(email) {
  return useAuthStore.getState().isAccountConnected(email);
}

// Legacy method - Refresh access token (prompts re-auth)
export async function refreshAccessToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user signed in');
  }
  return refreshAccountToken(user.email);
}

// Listen for auth state changes
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

// Load linked accounts from Firestore (call on app init)
export async function loadLinkedAccountsFromFirestore() {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return [];
  }

  try {
    const linkedAccounts = await firestoreService.getLinkedAccountsFromSubcollection(currentUser.uid);
    return linkedAccounts;
  } catch (error) {
    console.error('Failed to load linked accounts from Firestore:', error);
    return [];
  }
}

// Get linked accounts from Firestore subcollection
export async function getLinkedAccountsFromFirestore(userId) {
  return firestoreService.getLinkedAccountsFromSubcollection(userId);
}

// Re-authenticate a specific linked account (requires user interaction)
export async function reauthenticateAccount(email) {
  try {
    const provider = new GoogleAuthProvider();
    GOOGLE_SCOPES.forEach(scope => provider.addScope(scope));
    provider.setCustomParameters({
      prompt: 'none',  // Try silent first
      login_hint: email
    });

    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const accessToken = credential.accessToken;

    // Store token in memory
    useAuthStore.getState().setAccountToken(result.user.email, accessToken, {
      name: result.user.displayName,
      picture: result.user.photoURL,
      uid: result.user.uid,
    });

    // Persist re-authenticated token to Firestore
    const currentUser = auth.currentUser;
    if (currentUser) {
      firestoreService.saveGoogleToken(currentUser.uid, result.user.email, accessToken).catch(() => {});
    }

    return {
      success: true,
      email: result.user.email,
      accessToken
    };
  } catch (error) {
    // If silent fails, try with consent prompt
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Authentication cancelled');
    }

    // Try again with select_account prompt
    try {
      const provider = new GoogleAuthProvider();
      GOOGLE_SCOPES.forEach(scope => provider.addScope(scope));
      provider.setCustomParameters({
        prompt: 'select_account',
        login_hint: email
      });

      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;

      useAuthStore.getState().setAccountToken(result.user.email, accessToken, {
        name: result.user.displayName,
        picture: result.user.photoURL,
        uid: result.user.uid,
      });

      // Persist re-authenticated token to Firestore
      const currentUser2 = auth.currentUser;
      if (currentUser2) {
        firestoreService.saveGoogleToken(currentUser2.uid, result.user.email, accessToken).catch(() => {});
      }

      return {
        success: true,
        email: result.user.email,
        accessToken
      };
    } catch (retryError) {
      console.error('Re-authentication failed:', retryError);
      throw retryError;
    }
  }
}

// Initialize all linked accounts on app start
// Returns: { authenticated: [...emails], needsReauth: [...emails] }
export async function initializeLinkedAccounts() {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return { authenticated: [], needsReauth: [] };
  }

  useAuthStore.getState().setReauthenticating(true);

  try {
    // Load linked accounts from Firestore subcollection
    const linkedAccounts = await firestoreService.getLinkedAccountsFromSubcollection(currentUser.uid);

    const authenticated = [];
    const needsReauth = [];

    // Try to silently authenticate each linked account
    for (const account of linkedAccounts) {
      try {
        const provider = new GoogleAuthProvider();
        GOOGLE_SCOPES.forEach(scope => provider.addScope(scope));
        provider.setCustomParameters({
          prompt: 'none',
          login_hint: account.email
        });

        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);

        if (credential?.accessToken) {
          useAuthStore.getState().setAccountToken(result.user.email, credential.accessToken, {
            name: result.user.displayName,
            picture: result.user.photoURL,
            uid: result.user.uid,
          });
          authenticated.push(account.email);
        } else {
          needsReauth.push(account.email);
        }
      } catch {
        // Silent auth failed - user needs to manually re-authenticate
        needsReauth.push(account.email);
      }
    }

    return { authenticated, needsReauth, linkedAccounts };
  } finally {
    useAuthStore.getState().setReauthenticating(false);
  }
}

export default {
  signInWithGoogle,
  addGoogleAccount,
  refreshAccountToken,
  disconnectAccount,
  signOutUser,
  getCurrentUser,
  isAuthenticated,
  getGoogleAccessToken,
  getAccountToken,
  getConnectedAccounts,
  isAccountConnected,
  refreshAccessToken,
  onAuthChange,
  loadLinkedAccountsFromFirestore,
  getLinkedAccountsFromFirestore,
  reauthenticateAccount,
  initializeLinkedAccounts
};
